import React, { useEffect, useState, useCallback } from 'react'
import styles from './index.module.css'
import { groupsDoVdModalModes } from '../../../assets/const'
import { Main, Structure, TermTypes } from './ModalTabs'
import Modal from '../../../components/Modal'
import Confirmation from '../../../components/Confirmation/Confirmation'
import { GroupsDoVdContext } from '../../../utils/context'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { createNotification } from '../../../components/Notifications/Notifications'
import { createGroup, getGroupItem, updateGroup, getGroupsList, addTermType, addStudentsToGroup, deleteTermType, updateTermType } from '../../../actions/groupsDoVd'
import ModalFooter from '../../../components/ModalFooter/ModalFooter'
import * as Yup from 'yup'
import Loader from '../../../components/Loader'

export default function ModalComponent({ id, type, closeModal, mode, setMode, params, schoolYears, termTypes }) {
    const dispatch = useDispatch();

    const [confirmClose, setConfirmClose] = useState(false)
    const handleClose = useCallback(() => setConfirmClose(true), [])
    const acceptConfirmation = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
    const rejectConfirmation = useCallback(() => setConfirmClose(false), [])

    const validationSchema = Yup.object({
        name: Yup.string().required(' '),
        course_id: Yup.string().required(' '),
    });

    const { item, loadingItem, courses } = useSelector(state => state.groups_do_vd);
    const [tabs, setTabs] = useState(groupsDoVdModalModes);

    const courseIdField = type === 0 ? 'xp_cid' : 'out_cid';
    const groupIdField = type === 0 ? 'xp_group_id' : 'out_group_id';

    const [saving, setSaving] = useState(false);
    const [deletedTermTypes, setDeletedTermTypes] = useState([]);
    const [updatedTermTypes, setUpdatedTermTypes] = useState([]);

    const handleSave = (id) => {
        return async (values) => {
            setSaving(true);

            const groupData = {
                name: values.name,
                [courseIdField]: values.course_id ? Number(values.course_id) : null,
                closed: values.closed ? 1 : 0
            };

            let groupId = null;

            // 1. Создаём или обновляем группу
            if (id === 'add') {
                groupId = await dispatch(createGroup(groupData, type));
            } else {
                groupId = await dispatch(updateGroup(id, groupData, type));
            }

            if (!groupId) {
                setSaving(false);
                return;
            }

            // 2a. Удаляем типы уч. периодов, которые были удалены из списка
            for (const termTypeId of deletedTermTypes) {
                await dispatch(deleteTermType(termTypeId, type));
            }

            // 2b. Обновляем отредактированные типы уч. периодов
            for (const termType of updatedTermTypes) {
                const termData = {
                    term_type_id: termType.term_type_id,
                    s_year_id: termType.s_year_id,
                    [groupIdField]: Number(groupId)
                };
                await dispatch(updateTermType(termType.xp_group_tt_id, termData, type));
            }

            // 2c. Сохраняем типы уч. периодов (только новые, без id от сервера)
            const newTermTypes = values.term_types?.filter(t => !t.xp_group_tt_id && typeof t.id === 'number') || [];
            for (const termType of newTermTypes) {
                const termData = {
                    term_type_id: termType.term_type_id,
                    s_year_id: termType.s_year_id,
                    [groupIdField]: Number(groupId)
                };
                await dispatch(addTermType(termData, type));
            }

            // 3. Сохраняем состав (только новые, добавленные локально)
            const newStudents = values.structure?.filter(s => s.enroll_date && !s.isFromServer) || [];
            if (newStudents.length > 0) {
                const structureData = {
                    xp_date: newStudents[0].enroll_date,
                    mids: newStudents.map(s => s.mid)
                };
                await dispatch(addStudentsToGroup(groupId, structureData, type));
            }

            // 4. Обновляем список и закрываем модал
            await dispatch(getGroupsList(type, params));
            createNotification('success', id === 'add' ? 'Группа создана' : 'Группа обновлена');
            setSaving(false);
            closeModal();
        };
    };

    // Нормализуем данные о составе из API (number_of_trainees -> structure)
    const normalizeStructure = (data) => {
        if (!data) return [];
        return data.map(s => ({
            mid: s.mid,
            fio: s.fio,
            school_class: s.clas || s.school_class || '',
            enroll_date: s.enterdate || s.enroll_date || '',
            isFromServer: true // для отличия от локально добавленных
        }));
    };

    const formik = useFormik({
        initialValues: id !== 'add' ? {
            name: item?.name || '',
            course_id: item?.cid || item?.xp_cid || item?.out_cid || '',
            closed: item?.closed === 1,
            structure: normalizeStructure(item?.number_of_trainees),
            term_types: item?.study_period_type || []
        } : {
            name: '',
            course_id: '',
            closed: false,
            structure: [],
            term_types: []
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: handleSave(id),
    });

    useEffect(() => {
        if (Object.keys(formik.errors).length !== 0) {
            setTabs(tabs.map((tab) => (tab.value === 'main' ? { ...tab, error: true } : tab)));
        } else {
            setTabs(tabs.map((tab) => (tab.value === 'main' ? { ...tab, error: false } : tab)));
        }
    }, [formik.errors]);

    const error = (fieldName) => (formik.touched[fieldName] || formik.submitCount) && formik.errors[fieldName];

    const renderTab = () => {
        switch (mode) {
            case 'main': return <Main />
            case 'structure': return <Structure />
            case 'term_types': return <TermTypes />
            default: return null
        }
    };

    useEffect(() => {
        if (id && id !== 'add') {
            dispatch(getGroupItem(id, type));
        }
        // Сбрасываем списки удалённых/обновлённых при открытии/смене записи
        setDeletedTermTypes([]);
        setUpdatedTermTypes([]);
    }, [id, type]);

    return (
        <GroupsDoVdContext.Provider value={{
            id,
            type,
            closeModal,
            formik,
            schoolYears,
            termTypes,
            courses,
            error,
            courseIdField,
            groupIdField,
            params,
            saving,
            onDeleteTermType: (termTypeId) => setDeletedTermTypes(prev => [...prev, termTypeId]),
            onUpdateTermType: (item) => setUpdatedTermTypes(prev => {
                // Если уже есть в списке - обновляем, иначе добавляем
                const exists = prev.find(t => t.xp_group_tt_id === item.xp_group_tt_id);
                if (exists) {
                    return prev.map(t => t.xp_group_tt_id === item.xp_group_tt_id ? item : t);
                }
                return [...prev, item];
            })
        }}>
            {(loadingItem || saving) ? (
                <div className={styles.loader}><Loader /></div>
            ) : (
                <Modal
                    open={!!id}
                    id={id}
                    menuItem={mode}
                    setMenuItem={setMode}
                    menuList={tabs}
                    closeModal={closeModal}
                    onClose={handleClose}
                    closeOnOverlayClick={true}
                    closeOnEsc={true}
                >
                    {renderTab()}
                    <ModalFooter closeModal={closeModal} handleSubmit={formik.handleSubmit} />
                    {confirmClose && (
                        <Confirmation
                            title="Закрыть без сохранения изменений?"
                            acceptConfirmation={acceptConfirmation}
                            rejectConfirmation={rejectConfirmation}
                        />
                    )}
                </Modal>
            )}
        </GroupsDoVdContext.Provider>
    );
}
